import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card, { CardProps } from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import { SvgIconProps } from '@mui/material/SvgIcon';
import FilterListIcon from '@mui/icons-material/FilterList';
import DataTable, {
  DataTableWrapper,
  getActionsColConfig,
} from 'src/components/DataTable';
import { useStoreUnitActions } from 'src/redux/helpers';
import DataTableSearchBar from 'src/components/DataTable/components/DataTableSearchBar';
import { FilterTableContext } from 'src/components/DataTable/components/FilterTableContext';
import { ReactComponent as Assigned } from 'src/assets/icons/assigned.svg';
import { ReactComponent as Incident } from 'src/assets/icons/incident.svg';
import { ReactComponent as InProgress } from 'src/assets/icons/inProgress.svg';
import { ReactComponent as QueueWaiting } from 'src/assets/icons/queue-waiting.svg';
import {
  INCIDENT_PRIORITY,
  INCIDENT_STATE,
} from 'src/pages/Solutions/SolutionAlertRules/constant';
import { incidentItem, IncidentListItemField } from '../types';
import alertsStore, { useIncidentsList } from '../redux';
import AlertListAction from './AlertListAction';

type Props = CardProps<any> & {
  title?: string;
  description?: string;
  Icon: React.JSXElementConstructor<SvgIconProps>;
};

const columns = [
  {
    id: IncidentListItemField.Number,
    title: 'Incident Number',
  },
  {
    id: IncidentListItemField.ShortDescription,
    title: 'Description',
  },
  {
    id: IncidentListItemField.Priority,
    title: 'Priority',
  },
  {
    id: IncidentListItemField.State,
    title: 'State',
  },
  {
    id: IncidentListItemField.AssignedTo,
    title: 'Assigned To',
  },
  {
    id: IncidentListItemField.CreatedDateTime,
    title: 'Created',
  },
  {
    id: IncidentListItemField.UpdatedDateTime,
    title: 'Updated',
  },
  getActionsColConfig(AlertListAction),
];

const filteredColumns = [
  {
    id: IncidentListItemField.Number,
    title: 'Incident Number',
  },
  {
    id: IncidentListItemField.Priority,
    title: 'Priority',
  },
  {
    id: IncidentListItemField.State,
    title: 'State',
  },
];

const filterMenu = {
  priority: INCIDENT_PRIORITY,
  state: INCIDENT_STATE,
};

function IncidentCardList() {
  const [data, loading] = useIncidentsList();
  const actions = useStoreUnitActions(alertsStore);
  const [showFilters, setShowFilter] = useState(false);
  const [filterString, setFilterString] = useState('');
  const [filterData, setFilterData] = useState<incidentItem[]>([]);
  const [state, setState] = useState({
    new: 0,
    inProgress: 0,
    assigned: 0,
    onHold: 0,
  });
  const rowData = data;

  useEffect(() => {
    actions.alertsList();
  }, []);

  useEffect(() => {
    if (!data) return;
    const newIncidents = data?.filter((alert) => alert.state === 'New').length;
    const inProgressIncident = data?.filter(
      (alert) => alert.state === 'In Progress'
    ).length;
    const assignedIncident = data?.filter(
      (alert) => alert.assignedTo !== null && alert.assignedTo !== ''
    ).length;
    const onHoldIncident = data?.filter(
      (alert) => alert.state === 'On Hold'
    ).length;

    setState((prev) => {
      if (
        prev.new === newIncidents &&
        prev.inProgress === inProgressIncident &&
        prev.assigned === assignedIncident &&
        prev.onHold === onHoldIncident
      ) {
        return prev;
      }
      return {
        new: newIncidents,
        inProgress: inProgressIncident,
        assigned: assignedIncident,
        onHold: onHoldIncident,
      };
    });
  }, [data]);

  const cardData = [
    { title: 'New Incidents', description: state.new, icon: Incident },
    { title: 'In Progress', description: state.inProgress, icon: InProgress },
    { title: 'Assigned', description: state.assigned, icon: Assigned },
    { title: 'On Hold', description: state.onHold, icon: QueueWaiting },
  ];

  const handleClick = (event) => {
    event.preventDefault();
    setShowFilter((prevs) => !prevs);
  };

  function toggleshowfilter() {
    setShowFilter((prevs) => !prevs);
  }
  function scrollerUpdateWithFilter(filterstrings) {
    setFilterString(filterstrings);
  }

  const filterContextValue = React.useMemo(
    () => ({
      showFilters,
      toggleshowfilter,
      scrollerUpdateWithFilter,
      filterData,
      setFilterData,
      rowData,
    }),
    [showFilters, filterData, rowData]
  );

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'nowrap',
          gap: 2,
          mb: 2,
        }}
      >
        {cardData?.map((card) => (
          <Card
            key={card.title}
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: 400,
              minHeight: 90,
              px: 2,
              py: 2,
              boxShadow: 'none',
              border: '1px solid #e0e0e0',
            }}
          >
            <Box
              sx={{
                width: 48,
                height: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mr: 2,
              }}
            >
              <card.icon style={{ width: 32, height: 32 }} />
            </Box>
            <Box>
              <Typography fontSize="14px" sx={{ color: '#1E1E1E80' }}>
                {card.title}
              </Typography>
              <Typography
                sx={{ fontWeight: 500, fontSize: '26px', color: '#434343' }}
              >
                {card.description}
              </Typography>
            </Box>
          </Card>
        ))}
      </Box>
      <FilterTableContext.Provider value={filterContextValue}>
        <DataTableWrapper
          searchBar={false}
          actionsPanel={
            <Box>
              <Button
                onClick={handleClick}
                sx={{
                  padding: '8px',
                  marginRight: '7px',
                  marginLeft: '-8px',
                }}
                data-testid="filter-button"
              >
                <FilterListIcon />
              </Button>
              <DataTableSearchBar style={{ paddingRight: '10px' }} />
            </Box>
          }
        >
          <DataTable
            data={filterString ? filterData : data}
            loading={loading}
            columns={columns}
            filteredColumns={filteredColumns}
            filterMenus={filterMenu}
            columnTypes={{
              number: 'textfield',
              priority: 'select',
              state: 'select',
            }}
            actionName={actions.alertsList}
          />
        </DataTableWrapper>
      </FilterTableContext.Provider>
    </>
  );
}

export default IncidentCardList;
